import { useState, useRef, useEffect } from "react";
import { Language, SummaryContent, Person } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Headphones, Trash, Upload } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { fetchPeopleRoles } from "@/data/mockData";
import { fetchPeopleByRole } from "@/api/people";
import { MediaPicker } from "@/components/ui";

interface AudioUploaderProps {
  currentSummaryContent?: SummaryContent;
  languages: Language[];
  selectedLanguage: string;
  onAudioChange: (
    audioProperties: {
      audioTitle?: string;
      audioNarrator?: Person;
      audioDuration?: number;
      audioUrl?: string;
    } | null
  ) => void;
}

const AudioUploader = ({
  currentSummaryContent,
  languages,
  selectedLanguage,
  onAudioChange,
}: AudioUploaderProps) => {
  // Fetch roles to get Narrator role ID
  const { data: roles } = useQuery({
    queryKey: ["peopleRoles"],
    queryFn: fetchPeopleRoles,
  });

  // Find the Narrator role ID
  const narratorRoleId = roles?.find((role) => role.name === "Narrator")?.id;

  // Fetch narrators specifically using fetchPeopleByRole
  const { data: narratorsData } = useQuery({
    queryKey: ["narrators", narratorRoleId],
    queryFn: () => {
      if (!narratorRoleId) return Promise.resolve([]);
      return fetchPeopleByRole(narratorRoleId);
    },
    enabled: !!narratorRoleId,
  });

  // Check if audio properties exist in the SummaryContent
  const hasAudio = !!(
    currentSummaryContent?.audioNarrator ||
    currentSummaryContent?.audioTitle ||
    currentSummaryContent?.audioUrl
  );

  const [audioTitle, setAudioTitle] = useState(
    currentSummaryContent?.audioTitle || ""
  );
  const [audioNarrator, setAudioNarrator] = useState<Person | undefined>(
    currentSummaryContent?.audioNarrator
  );
  const [audioDuration, setAudioDuration] = useState(
    currentSummaryContent?.audioDuration || 0
  );
  const [audioUrl, setAudioUrl] = useState(
    currentSummaryContent?.audioUrl || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [loadingDuration, setLoadingDuration] = useState(false);

  // Update form when language or summary content changes
  useEffect(() => {
    setAudioTitle(currentSummaryContent?.audioTitle || "");
    setAudioNarrator(currentSummaryContent?.audioNarrator);
    setAudioDuration(currentSummaryContent?.audioDuration || 0);
    setAudioUrl(currentSummaryContent?.audioUrl || "");
    setIsSaving(false);
    setHasChanged(false);

    // If we have a URL but no title, try to extract title from URL
    if (
      currentSummaryContent?.audioUrl &&
      (!currentSummaryContent?.audioTitle ||
        currentSummaryContent?.audioTitle.trim() === "")
    ) {
      extractTitleFromUrl(currentSummaryContent.audioUrl);
    }
  }, [selectedLanguage, currentSummaryContent]);

  // Helper function to extract title from URL
  const extractTitleFromUrl = (url: string) => {
    if (!url || url.trim() === "") return;

    try {
      // Try with URL object first
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = decodeURIComponent(pathname.split("/").pop() || "");

      // Clean up the filename to make a nice title
      const cleanTitle = filename
        .replace(/\.[^/.]+$/, "") // Remove file extension
        .replace(/^(audio-|audio_|mp3-|mp3_)/, "") // Remove common prefixes
        .replace(/[0-9]{4}$/, "") // Remove trailing 4-digit numbers (timestamps)
        .replace(/[-_]/g, " ") // Replace hyphens and underscores with spaces
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .trim(); // Remove leading/trailing spaces

      if (cleanTitle) {
        console.log("Extracted title from URL:", cleanTitle);
        setAudioTitle(cleanTitle);
        return cleanTitle;
      }
    } catch (error) {
      // URL parsing failed, try a simpler approach
      try {
        const parts = url.split("/");
        const lastPart = decodeURIComponent(parts[parts.length - 1]);
        if (lastPart) {
          const cleanTitle = lastPart
            .replace(/\.[^/.]+$/, "") // Remove file extension
            .replace(/^(audio-|audio_|mp3-|mp3_)/, "") // Remove common prefixes
            .replace(/[0-9]{4}$/, "") // Remove trailing 4-digit numbers (timestamps)
            .replace(/[-_]/g, " ") // Replace hyphens and underscores with spaces
            .replace(/\s+/g, " ") // Replace multiple spaces with single space
            .trim(); // Remove leading/trailing spaces

          if (cleanTitle) {
            console.log("Extracted title from URL (fallback):", cleanTitle);
            setAudioTitle(cleanTitle);
            return cleanTitle;
          }
        }
      } catch (e) {
        console.error("Failed to extract title from URL:", e);
      }
    }

    return null;
  };

  // Update the parent component when audio fields change with debouncing
  useEffect(() => {
    // Don't trigger save if we're just initializing with the original values
    if (!hasChanged) return;

    setIsSaving(true);
    const debounceTimeout = setTimeout(() => {
      onAudioChange({
        audioTitle: audioTitle || undefined,
        audioNarrator: audioNarrator,
        audioDuration: audioDuration || undefined,
        audioUrl: audioUrl.trim() === "" ? undefined : audioUrl,
      });
      setIsSaving(false);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimeout);
  }, [
    audioTitle,
    audioNarrator,
    audioDuration,
    audioUrl,
    onAudioChange,
    hasChanged,
  ]);

  // Handle changes to any audio fields
  const handleFieldChange = (
    field: string,
    value: string | number | Person | undefined
  ) => {
    setHasChanged(true);

    // Declare variables outside switch for use in case blocks
    let urlValue: string;
    let extractedTitle: string | null = null;

    switch (field) {
      case "title":
        setAudioTitle(value as string);
        break;
      case "narrator":
        setAudioNarrator(value as Person | undefined);
        break;
      case "duration":
        setAudioDuration(value as number);
        break;
      case "url":
        // For URL, treat empty string as an actual value for display
        // but will be converted to undefined when saving
        urlValue = value as string;
        setAudioUrl(urlValue);

        // If URL is cleared, reset title and duration
        if (!urlValue || urlValue.trim() === "") {
          setAudioTitle("");
          setAudioDuration(0);

          // Also notify parent component to update immediately
          onAudioChange({
            audioTitle: undefined,
            audioDuration: undefined,
            audioUrl: undefined,
            audioNarrator: audioNarrator, // Keep narrator if set
          });
        } else {
          // Try to extract a title from the URL
          extractedTitle = extractTitleFromUrl(urlValue);

          // Immediately save changes with the extracted title (if any)
          onAudioChange({
            audioTitle: extractedTitle || audioTitle || undefined,
            audioNarrator: audioNarrator,
            audioDuration: audioDuration || undefined,
            audioUrl: urlValue,
          });
        }
        break;
    }
  };

  const removeAudio = () => {
    // Remove audio by explicitly setting all audio fields to undefined
    // This ensures they get properly nulled out in the database
    onAudioChange({
      audioTitle: undefined,
      audioNarrator: undefined,
      audioDuration: undefined,
      audioUrl: undefined,
    });

    // Clear form
    setAudioTitle("");
    setAudioNarrator(undefined);
    setAudioDuration(0);
    setAudioUrl(""); // Empty for display purposes
    setIsSaving(false);
    setHasChanged(false);
  };

  const selectedLanguageName =
    languages.find((lang) => lang.code === selectedLanguage)?.name ||
    selectedLanguage;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">
          {hasAudio
            ? currentSummaryContent?.audioUrl
              ? `Audio for ${selectedLanguageName}`
              : `Narrator for ${selectedLanguageName}`
            : ""}
        </h3>
        <div className="flex items-center gap-2">
          {hasAudio && (
            <Button
              variant="outline"
              size="sm"
              onClick={removeAudio}
              className="h-8"
            >
              <Trash className="h-4 w-4 mr-2" /> Remove
            </Button>
          )}
        </div>
      </div>

      {/* Replace the old audio file upload with MediaPicker */}
      <div className="space-y-2">
        <Label>Audio File</Label>
        <MediaPicker
          onSelect={(url) => {
            console.log("MediaPicker selected URL:", url);
            handleFieldChange("url", url);
          }}
          currentValue={audioUrl}
          allowedTypes={["audio/mpeg", "audio/mp3", "audio/wav", "audio/ogg"]}
          title="Select Audio File"
          description="Choose an audio file from your media library or upload a new one"
          buttonText="Select Audio File"
        />
      </div>

      {/* Only show the following fields when audioUrl exists */}
      {audioUrl && (
        <>
          {/* Audio Player */}
          <div className="pt-1 pb-2">
            <audio
              controls
              className="w-full theme-audio-player"
              preload="metadata"
              src={audioUrl}
              onLoadedMetadata={(e) => {
                const audioEl = e.currentTarget;
                if (audioEl.duration && audioEl.duration !== Infinity) {
                  // const durationInMinutes =
                  //   Math.round((audioEl.duration / 60) * 10) / 10;
                  // console.log(
                  //   "Setting duration from audio player:",
                  //   durationInMinutes
                  // );

                  const durationInSeconds = Math.floor(audioEl.duration);

                  setAudioDuration(durationInSeconds);
                  onAudioChange({
                    audioTitle: audioTitle,
                    audioNarrator: audioNarrator,
                    audioDuration: durationInSeconds,
                    audioUrl: audioUrl,
                  });

                  setLoadingDuration(false);
                  setHasChanged(true);
                }
              }}
            />
          </div>

          {/* Title and Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="audioTitle">Title</Label>
              <Input
                id="audioTitle"
                value={audioTitle}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                placeholder="Enter audio title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="audioDuration">Duration (seconds)</Label>
              <div className="relative">
                <Input
                  id="audioDuration"
                  type="number"
                  value={audioDuration}
                  onChange={(e) =>
                    handleFieldChange("duration", parseInt(e.target.value) || 0)
                  }
                  placeholder={
                    loadingDuration
                      ? "Extracting duration..."
                      : "Enter audio duration"
                  }
                  disabled={loadingDuration}
                  className="w-full"
                />
                {loadingDuration && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Narrator - Now only shown when audio URL exists */}
          <div className="space-y-2">
            <Label htmlFor="audioNarrator">Narrator</Label>
            <Select
              value={String(audioNarrator?.id || "none")}
              onValueChange={(value) => {
                if (value === "none") {
                  handleFieldChange("narrator", undefined);
                } else {
                  const selectedNarrator = narratorsData?.find(
                    (person) => person.id === Number(value)
                  );
                  if (selectedNarrator) {
                    handleFieldChange("narrator", selectedNarrator);
                  }
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select narrator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {narratorsData?.map((person) => (
                  <SelectItem
                    key={person.id.toString()}
                    value={person.id.toString()}
                  >
                    {person.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </div>
  );
};

export default AudioUploader;
